/**
 * Intake numbering: the ONLY place client number, matter number, and matter name can be
 * entered now. Enforced server-side by the setMatterNumbers callable (requireIntake in
 * functions/index.js) — this page is reachable only via RequireIntake in App.jsx, but the
 * backend permission change is what actually makes this exclusive, not this page.
 *
 * Lists matters that are fully approved, or directly submitted while workflow is
 * disabled, and still missing a number. It has editable Client Number / Matter Number / Matter Name fields and a
 * save button per row. Client-side validation blocks saving an empty number or a number
 * that duplicates another matter's already-assigned number (numberingService.js does
 * not enforce uniqueness server-side today, so this page is the one place doing it).
 */

import { useState } from 'react';
import { CallStatus, useCallable } from '../lib/useCallable';
import { useCollection } from '../lib/useFirestore';

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';

export default function IntakeNumbering() {
  const matters = useCollection('matters');
  const { run, busy, error, saved } = useCallable();
  const [drafts, setDrafts] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  const needing = matters.data.filter(
    (m) => ['approved', 'submitted'].includes(m.status) && (!m.clientNumber || !m.matterNumber)
  );

  const setDraft = (id, key, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const clearRowError = (id) =>
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const validate = (matter) => {
    const clientNumber = (drafts[matter.id]?.clientNumber ?? matter.clientNumber ?? '').trim();
    const matterNumber = (drafts[matter.id]?.matterNumber ?? matter.matterNumber ?? '').trim();
    const matterName = (
      drafts[matter.id]?.matterName ?? matter.filesToBeOpened?.matterName ?? ''
    ).trim();

    if (!clientNumber || !matterNumber || !matterName) {
      return 'Client number, matter number, and matter name are all required.';
    }

    const clashesWithClientNumber = matters.data.some(
      (other) => other.id !== matter.id && other.clientNumber && other.clientNumber === clientNumber
    );
    if (clashesWithClientNumber) {
      return `Client number "${clientNumber}" is already used by another matter.`;
    }
    const clashesWithMatterNumber = matters.data.some(
      (other) => other.id !== matter.id && other.matterNumber && other.matterNumber === matterNumber
    );
    if (clashesWithMatterNumber) {
      return `Matter number "${matterNumber}" is already used by another matter.`;
    }
    return null;
  };

  const save = async (matter) => {
    const validationError = validate(matter);
    if (validationError) {
      setRowErrors((prev) => ({ ...prev, [matter.id]: validationError }));
      return;
    }
    clearRowError(matter.id);

    const clientNumber = (drafts[matter.id]?.clientNumber ?? matter.clientNumber ?? '').trim();
    const matterNumber = (drafts[matter.id]?.matterNumber ?? matter.matterNumber ?? '').trim();
    const matterName = (
      drafts[matter.id]?.matterName ?? matter.filesToBeOpened?.matterName ?? ''
    ).trim();

    await run('setMatterNumbers', {
      matterId: matter.id,
      clientNumber,
      matterNumber,
      matterName,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">Intake numbering</h1>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Client number, matter number, and matter name are entered here by Intake once a
        matter is fully approved or directly submitted with workflow disabled. No other role
        can edit these three fields.
      </p>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          Matters awaiting numbers
        </h2>

        {matters.loading ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : needing.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Nothing waiting.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-2">Client number</th>
                <th className="py-2 pr-2">Matter number</th>
                <th className="py-2 pr-2">Matter name</th>
                <th className="py-2" />
              </tr>
            </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {needing.map((matter) => (
                <tr key={matter.id}>
                  <td className="py-2 pr-3 align-top">
                    <div className="text-slate-800 dark:text-slate-100">{matter.client?.name || 'Untitled'}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-500">{matter.id}</div>
                  </td>
                  <td className="w-40 py-2 pr-2 align-top">
                    <input
                      className={field}
                      placeholder="Client number"
                      value={drafts[matter.id]?.clientNumber ?? matter.clientNumber ?? ''}
                      onChange={(e) => {
                        clearRowError(matter.id);
                        setDraft(matter.id, 'clientNumber', e.target.value);
                      }}
                    />
                  </td>
                  <td className="w-40 py-2 pr-2 align-top">
                    <input
                      className={field}
                      placeholder="Matter number"
                      value={drafts[matter.id]?.matterNumber ?? matter.matterNumber ?? ''}
                      onChange={(e) => {
                        clearRowError(matter.id);
                        setDraft(matter.id, 'matterNumber', e.target.value);
                      }}
                    />
                  </td>
                  <td className="w-56 py-2 pr-2 align-top">
                    <input
                      className={field}
                      placeholder="Matter name"
                      value={drafts[matter.id]?.matterName ?? matter.filesToBeOpened?.matterName ?? ''}
                      onChange={(e) => {
                        clearRowError(matter.id);
                        setDraft(matter.id, 'matterName', e.target.value);
                      }}
                    />
                  </td>
                  <td className="py-2 align-top">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => save(matter)}
                      className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1 text-sm text-white disabled:opacity-50"
                    >
                      Save
                    </button>
                    {rowErrors[matter.id] && (
                      <p className="mt-1 max-w-[14rem] text-xs text-red-600 dark:text-red-400">{rowErrors[matter.id]}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
